'use client';

import { useCallback, useState, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { useQueryClient } from '@tanstack/react-query';
import { getDependencies } from '@/lib/services/query-dependencies';
import { institutionKeys } from '@/lib/query-keys';
import { useFileUpload } from '@/lib/services/files';
import {
  updateFormStatusAction,
  deleteFormAction
} from '@/lib/services/institution-forms/mutations';
import { downloadInstitutionForm } from '@/lib/services/institution-forms/download';
import { toast } from '@/lib/toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  StatusBadge,
  type StatusBadgeTone
} from '@/components/ui/status-badge';
import { Banner } from '@/components/ui/banner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import {
  UploadIcon,
  DownloadIcon,
  MoreVerticalIcon,
  EyeIcon,
  SendIcon,
  TrashIcon,
  CheckCircleIcon,
  ClockIcon,
  MailIcon,
  PackageIcon,
  Plus,
  X,
  FileText,
  type LucideIcon
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { InstitutionForm } from '@/lib/services/institution-forms/types';
import type { AddDocumentTarget } from '@/components/shared/add-document';
import { useReplaceDocument } from '@/components/shared/replace-document';
import { acceptStringFor } from '@/lib/constants/file-type-accept';

/**
 * What a signed institution form lands as: the `institution-form-signed` file
 * type plus the association ids the backend needs to attach it to this form.
 *
 * One definition, used by both the upload-from-machine path and the shared
 * Replace seam (VALE-1135). They already have to agree (a replacement that
 * landed with a different `institutionFormId` would attach to the wrong form),
 * and stating the mapping twice is how they stop agreeing. The
 * template-then-own-id fallback is load-bearing: a form that has never been
 * uploaded is keyed by its template, an uploaded one by its own record.
 */
function institutionFormTarget(
  form: InstitutionForm,
  institutionId: number
): AddDocumentTarget {
  return {
    fileType: 'institution-form-signed',
    label: form.name,
    // The requirement has a canonical name of its own, so the signed form lands
    // as that rather than as whatever the scanner called the file.
    displayName: form.name,
    institutionId,
    institutionFormId: form.templateId
      ? Number(form.templateId)
      : Number(form.id),
    ...(form.signatoryContactId != null && {
      signatoryContactId: form.signatoryContactId
    }),
    ...(form.inventoryItemId != null && {
      inventoryItemId: form.inventoryItemId
    })
  };
}

/**
 * The overflow menu on an uploaded form row: view, mark sent, **Replace**, remove.
 *
 * Its own component so the shared Replace seam's hook can run once per row:
 * rows are produced by a `map`, and a hook cannot be. Replace used to be an
 * imperative `document.createElement('input')` here, the only one of the five
 * hand-rolled Replace pickers that did not even hold a ref; it offered the OS
 * picker and nothing else on a matter whose signed form may already sit in the
 * firm's PMS or on the matter itself.
 */
function UploadedFormActions({
  form,
  matterId,
  institutionId,
  isSent,
  onView,
  onMarkSent,
  onRemove,
  onReplaced
}: {
  form: InstitutionForm;
  matterId: number;
  institutionId: number;
  isSent: boolean;
  onView: () => void;
  onMarkSent: (method: 'email' | 'post') => void;
  onRemove: () => void;
  onReplaced: () => void;
}) {
  const replace = useReplaceDocument({
    matterId,
    target: institutionFormTarget(form, institutionId),
    replacingFileId: form.uploadedFileId,
    label: 'Replace Upload',
    onReplaced
  });

  return (
    <>
      {/* `modal={false}` because the Replace item opens a Dialog in the same
          React commit that closes this menu. A modal dropdown writes
          `pointer-events: none` onto `<body>`, and its restore races the
          dialog's own lock, leaving the whole page un-clickable until a
          refresh (`components/CLAUDE.md`, LF-4072). */}
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm">
            <MoreVerticalIcon className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {/* View uploaded form */}
          <DropdownMenuItem onClick={onView}>
            <EyeIcon className="mr-2 h-4 w-4" />
            View Form
          </DropdownMenuItem>

          {/* Mark as sent options if not already sent */}
          {!isSent && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => onMarkSent('email')}>
                <MailIcon className="mr-2 h-4 w-4" />
                Mark Sent (Email)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onMarkSent('post')}>
                <PackageIcon className="mr-2 h-4 w-4" />
                Mark Sent (Post)
              </DropdownMenuItem>
            </>
          )}

          {/* Replace upload option */}
          <DropdownMenuSeparator />
          {replace.menuItem}

          {/* Remove option if not sent */}
          {!isSent && (
            <DropdownMenuItem
              onClick={onRemove}
              className="text-error-subtle-foreground"
            >
              <TrashIcon className="mr-2 h-4 w-4" />
              Remove Upload
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {/* Outside the dropdown, which unmounts its children on select. */}
      {replace.surfaces}
    </>
  );
}

interface FormsTableSimplifiedProps {
  forms: InstitutionForm[];
  institutionName: string;
  matterId: number;
  institutionId: number;
  inventoryItemId?: number; // Optional inventory item association
}

export function FormsTableSimplified({
  forms,
  institutionName,
  matterId,
  institutionId,
  inventoryItemId
}: FormsTableSimplifiedProps) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const [selectedForm, setSelectedForm] = useState<InstitutionForm | null>(
    null
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showManualUpload, setShowManualUpload] = useState(false);

  // File upload hook
  const { uploadFile } = useFileUpload(matterId);

  // Per-form loading state using composite keys
  const [uploadingForms, setUploadingForms] = useState<Set<string>>(
    () => new Set()
  );
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>(
    {}
  );

  // Get simple status display. `tone` replaces the former per-state class
  // paste (LF-6155): every "sent" variant is the same `info` state — the
  // channel is named by the icon and the label, which is what distinguished
  // the old blue / purple / orange trio in the first place.
  const getStatusDisplay = (
    form: InstitutionForm
  ): { label: string; icon: LucideIcon; tone: StatusBadgeTone } => {
    // Check if sent (either via email or post)
    if (form.status === 'sent' || form.sentViaEmailAt || form.sentViaPostAt) {
      const sentViaEmail = !!form.sentViaEmailAt;
      const sentViaPost = !!form.sentViaPostAt;

      if (sentViaEmail && sentViaPost) {
        return {
          label: 'Sent (Email & Post)',
          icon: CheckCircleIcon,
          tone: 'info'
        };
      } else if (sentViaEmail) {
        return { label: 'Sent via Email', icon: MailIcon, tone: 'info' };
      } else if (sentViaPost) {
        return { label: 'Sent via Post', icon: PackageIcon, tone: 'info' };
      }
      return { label: 'Sent', icon: SendIcon, tone: 'info' };
    }

    switch (form.status) {
      case 'not_uploaded':
        return {
          label: 'Pending Upload',
          icon: ClockIcon,
          tone: 'caution'
        };
      case 'uploaded':
        return { label: 'Uploaded', icon: CheckCircleIcon, tone: 'success' };
      default:
        return { label: form.status, icon: ClockIcon, tone: 'neutral' };
    }
  };

  // Handle form download (blank template or uploaded form)
  const handleDownload = async (form: InstitutionForm, isUploaded = false) => {
    try {
      // For uploaded files, use direct file download endpoint
      if (isUploaded && form.uploadedFileId) {
        const downloadUrl = `/api/proxy/files/${form.uploadedFileId}/download`;
        window.open(downloadUrl, '_blank');
        toast.success('Opening uploaded form for download');
        return;
      }

      // For non-uploaded forms (blank templates), use the generated form download
      const result = await downloadInstitutionForm(
        matterId,
        institutionId,
        form.id,
        {
          signatoryContactId: form.signatoryContactId,
          inventoryItemId: form.inventoryItemId
        }
      );

      if (result.success) {
        toast.success('Opening form for download');
      } else {
        toast.error(result.error ?? 'Failed to download form');
      }
    } catch (_error) {
      toast.error('Failed to download form');
    }
  };

  /**
   * Everything a change to this institution's forms invalidates.
   *
   * LF-3216 / LF-3021: cascade via the single-source dependency map. Lifted out
   * of the three handlers that each held a copy (VALE-1135) so the Replace seam
   * refreshes identically. It lands the same file against the same form, and a
   * per-caller refresh list is how one path ends up leaving a stale row.
   */
  const refreshInstitution = useCallback(() => {
    for (const key of getDependencies(
      'institution',
      Number(matterId),
      institutionId
    )) {
      queryClient.invalidateQueries({ queryKey: key });
    }
    router.refresh();
  }, [queryClient, router, matterId, institutionId]);

  // Handle form upload
  const handleUpload = (form: InstitutionForm, formIndex: number) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      // Use form.id and index combination for upload state tracking
      // This matches the row key pattern for consistency
      const uploadStateKey = `${institutionId}_form_${form.id}_${formIndex}`;

      try {
        // Set loading state for this specific form using unique key
        setUploadingForms(
          (prev) => new Set([...Array.from(prev), uploadStateKey])
        );
        setUploadProgress((prev) => ({ ...prev, [uploadStateKey]: 0 }));

        // Upload using presigned URL with the same file type and association
        // ids the Replace seam lands with: one definition, above.
        const {
          fileType,
          label: _label,
          ...uploadOptions
        } = institutionFormTarget(form, institutionId);

        const uploadResult = await uploadFile(file, fileType, uploadOptions);

        if (uploadResult) {
          // Note: Success toast is already shown by the upload mutation

          refreshInstitution();
        }
      } catch (error) {
        console.error('Upload error:', error);
        toast.error('Failed to upload form');
      } finally {
        // Clear loading state for this specific form using unique key
        setUploadingForms((prev) => {
          const newSet = new Set(Array.from(prev));
          newSet.delete(uploadStateKey);
          return newSet;
        });
        setUploadProgress((prev) => {
          const newProgress = { ...prev };
          delete newProgress[uploadStateKey];
          return newProgress;
        });
      }
    };
    input.click();
  };

  // Handle marking form as sent
  const handleMarkSent = async (
    form: InstitutionForm,
    method: 'email' | 'post'
  ) => {
    try {
      // Use the uploadedFormId for sent operations
      if (!form.uploadedFormId) {
        toast.error('Form must be uploaded before marking as sent');
        return;
      }

      // Call the mark-sent endpoint with the correct method
      await updateFormStatusAction(
        matterId,
        institutionId,
        String(form.uploadedFormId),
        method === 'email' ? 'sent_email' : 'sent_post'
      );

      toast.success(`Form marked as sent via ${method}`);

      refreshInstitution();
    } catch (_error) {
      toast.error('Failed to update form status');
    }
  };

  // Handle form deletion (remove uploaded form)
  const handleDelete = async () => {
    if (!selectedForm || !selectedForm.uploadedFormId) return;

    setIsDeleting(true);
    try {
      await deleteFormAction(
        matterId,
        institutionId,
        String(selectedForm.uploadedFormId)
      );

      toast.success('Form removed successfully');

      refreshInstitution();
    } catch (_error) {
      toast.error('Failed to remove form');
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setSelectedForm(null);
    }
  };

  // Group forms by signatory if they have one
  const _groupedForms = forms.reduce(
    (acc, form) => {
      const key = form.signatoryName || 'General Forms';
      if (!acc[key]) acc[key] = [];
      acc[key].push(form);
      return acc;
    },
    {} as Record<string, InstitutionForm[]>
  );

  return (
    <div className="space-y-4">
      {/* Simple instructions and upload button */}
      <div className="flex items-center justify-between gap-4">
        <div className="text-muted-foreground text-sm">
          Download required forms, have them signed by the appropriate parties,
          then upload the completed versions.
        </div>
        {!showManualUpload && (
          <Button
            size="xs"
            density="compact"
            gap="loose"
            variant="outline"
            onClick={() => setShowManualUpload(true)}
            className="shrink-0"
          >
            <Plus className="h-3.5 w-3.5" />
            Add Manual Form
          </Button>
        )}
      </div>

      {/* Manual upload section */}
      {showManualUpload && (
        <div className="bg-muted/30 rounded-md border p-6">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-sm font-medium">Upload Manual Forms</h3>
            <Button
              gap="loose"
              variant="ghost"
              size="sm"
              onClick={() => setShowManualUpload(false)}
            >
              <X className="h-4 w-4" />
              Cancel
            </Button>
          </div>
          <UploadFormsSection
            matterId={matterId}
            institutionId={institutionId}
            institutionName={institutionName}
            inventoryItemId={inventoryItemId}
            onUploadComplete={() => {
              setShowManualUpload(false);
              // Invalidate forms cache
              queryClient.invalidateQueries({
                queryKey: institutionKeys.forms(matterId, institutionId)
              });
              router.refresh();
            }}
          />
        </div>
      )}

      {/* Forms table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[350px] min-w-[200px]">Form</TableHead>
              <TableHead className="w-[180px]">Signatory</TableHead>
              <TableHead className="w-[150px]">Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {forms.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={4}
                  className="text-muted-foreground py-8 text-center"
                >
                  No forms are available for this institution yet. You can
                  upload the forms manually to attach them to the collection
                  correspondence.
                </TableCell>
              </TableRow>
            ) : (
              forms.map((form, index) => {
                const status = getStatusDisplay(form);
                const StatusIcon = status.icon;
                const isUploaded =
                  form.status === 'uploaded' || form.status === 'sent';
                const isSent =
                  form.status === 'sent' ||
                  form.sentViaEmailAt ||
                  form.sentViaPostAt;

                // Create unique key for this form instance
                // Use index to guarantee uniqueness for React rendering
                // This ensures each row is unique even if forms have duplicate IDs
                const uniqueFormKey = `${institutionId}_form_${form.id}_${index}`;

                return (
                  <TableRow key={uniqueFormKey}>
                    <TableCell className="max-w-[350px] p-2">
                      <div className="flex flex-col">
                        <span
                          className="font-medium break-words"
                          style={{ wordBreak: 'break-word', hyphens: 'auto' }}
                        >
                          {form.name}
                        </span>
                        <div className="mt-1 flex items-center gap-2">
                          {form.isFreeForm && (
                            <span className="text-muted-foreground text-xs">
                              Additional document
                            </span>
                          )}
                          {form.inventoryItemId && (
                            <Badge variant="secondary" className="">
                              Item #{form.inventoryItemId}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="p-2">
                      {form.signatoryName ? (
                        <div className="flex items-center gap-1.5 text-sm">
                          <span>{form.signatoryName}</span>
                          {form.signatoryType && (
                            <Badge variant="outline" className="ml-1">
                              {form.signatoryType}
                            </Badge>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm">—</span>
                      )}
                    </TableCell>
                    <TableCell className="p-2">
                      <StatusBadge tone={status.tone} icon={StatusIcon}>
                        {status.label}
                      </StatusBadge>
                    </TableCell>
                    <TableCell className="p-2">
                      <div className="flex items-center justify-end gap-2">
                        {/* Download button - always available */}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDownload(form, isUploaded)}
                          title={
                            isUploaded
                              ? 'Download uploaded form'
                              : 'Download blank template'
                          }
                        >
                          <DownloadIcon className="h-4 w-4" />
                        </Button>

                        {/* Upload button - for forms not yet uploaded */}
                        {!isUploaded && (
                          <Button
                            size="xs"
                            density="compact"
                            gap="standard"
                            variant="default"
                            onClick={() => handleUpload(form, index)}
                            disabled={uploadingForms.has(uniqueFormKey)}
                          >
                            <UploadIcon className="h-3.5 w-3.5" />
                            {uploadingForms.has(uniqueFormKey) &&
                            uploadProgress[uniqueFormKey] > 0
                              ? `${uploadProgress[uniqueFormKey]}%`
                              : uploadingForms.has(uniqueFormKey)
                                ? 'Uploading...'
                                : 'Upload'}
                          </Button>
                        )}

                        {/* Actions menu for uploaded forms */}
                        {isUploaded && (
                          <UploadedFormActions
                            form={form}
                            matterId={Number(matterId)}
                            institutionId={institutionId}
                            isSent={!!isSent}
                            onView={() => handleDownload(form, isUploaded)}
                            onMarkSent={(method) =>
                              handleMarkSent(form, method)
                            }
                            onRemove={() => {
                              setSelectedForm(form);
                              setDeleteDialogOpen(true);
                            }}
                            onReplaced={refreshInstitution}
                          />
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Info about physical mail if needed */}
      {forms.some((f) => f.requiresHardCopy) && (
        <Banner
          tone="neutral"
          title="Physical mail required"
          line={
            <>
              Some forms must be sent via post. After uploading, print and mail
              the signed forms to {institutionName}.
            </>
          }
        />
      )}

      {/* Delete confirmation dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Uploaded Form</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the uploaded file for "{selectedForm?.name}". The
              form will revert to pending status and you'll need to upload it
              again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-error hover:bg-error/90"
            >
              {isDeleting ? 'Removing...' : 'Remove'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Upload Forms Section Component
function UploadFormsSection({
  matterId,
  institutionId,
  institutionName: _institutionName,
  inventoryItemId,
  onUploadComplete
}: {
  matterId: number;
  institutionId: number;
  institutionName: string;
  inventoryItemId?: number;
  onUploadComplete?: () => void;
}) {
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadFile } = useFileUpload(matterId);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setSelectedFiles((prev) => [...prev, ...files]);
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    setIsUploading(true);
    try {
      // Upload each file as a signed institution form
      const uploadPromises = selectedFiles.map(async (file) => {
        const metadata: {
          displayName: string;
          institutionId: number;
          inventoryItemId?: number;
        } = {
          displayName: file.name,
          institutionId: institutionId
        };

        // Include inventoryItemId if provided
        if (inventoryItemId !== undefined && inventoryItemId !== null) {
          metadata.inventoryItemId = inventoryItemId;
        }

        return uploadFile(file, 'institution-form-signed', metadata);
      });

      // Wait for all uploads to complete
      await Promise.all(uploadPromises);

      toast.success(
        `Successfully uploaded ${selectedFiles.length} form${selectedFiles.length > 1 ? 's' : ''}`
      );
      setSelectedFiles([]);

      if (onUploadComplete) {
        onUploadComplete();
      }
    } catch (error) {
      toast.error('Failed to upload forms. Please try again.');
      console.error('Upload error:', error);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="w-full">
      {/* File input (hidden) */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        // A signed institution form is a scan: the backend's `institution-form-signed`
        // takes a PDF or a photo and nothing else. This used to offer `.doc`, `.docx`,
        // `.xls` and `.xlsx`, all four of which it refuses, while omitting the image
        // formats it accepts (VALE-209).
        accept={acceptStringFor('institution-form-signed')}
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Upload area */}
      <div
        className={cn(
          'cursor-pointer rounded-lg border-2 border-dashed p-6 text-center transition-colors',
          'hover:border-primary/50 hover:bg-muted/50',
          isUploading && 'cursor-not-allowed opacity-50'
        )}
        onClick={() => !isUploading && fileInputRef.current?.click()}
      >
        <UploadIcon className="text-muted-foreground mx-auto mb-3 h-8 w-8" />
        <p className="mb-1 text-sm font-medium">Click to upload forms</p>
        <p className="text-muted-foreground text-xs">
          PDF, DOC, DOCX, XLS, XLSX files accepted
        </p>
      </div>

      {/* Selected files list */}
      {selectedFiles.length > 0 && (
        <div className="mt-4 space-y-2">
          <p className="text-sm font-medium">Selected files:</p>
          {selectedFiles.map((file, index) => (
            <div
              key={`${file.name}-${file.size}-${file.lastModified}`}
              className="bg-muted/50 flex items-center justify-between rounded px-3 py-2"
            >
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <FileText className="text-muted-foreground h-4 w-4 shrink-0" />
                <span className="truncate text-sm">{file.name}</span>
                <span className="text-muted-foreground text-xs">
                  ({(file.size / 1024).toFixed(1)} KB)
                </span>
              </div>
              <Button
                variant="ghost"
                size="icon-2xs"
                onClick={(e) => {
                  e.stopPropagation();
                  removeFile(index);
                }}
                disabled={isUploading}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ))}

          {/* Upload button */}
          <Button
            size="xs"
            density="compact"
            gap="standard"
            width="full"
            onClick={handleUpload}
            disabled={isUploading || selectedFiles.length === 0}
            className="mt-3"
          >
            {isUploading ? (
              <>
                <div className="border-card h-4 w-4 animate-spin rounded-full border-b-2" />
                Uploading...
              </>
            ) : (
              <>
                <UploadIcon className="h-3.5 w-3.5" />
                Upload {selectedFiles.length} file
                {selectedFiles.length > 1 ? 's' : ''}
              </>
            )}
          </Button>
        </div>
      )}

      {/* Add another file button */}
      {selectedFiles.length > 0 && !isUploading && (
        <Button
          size="xs"
          density="compact"
          gap="standard"
          width="full"
          variant="outline"
          onClick={() => fileInputRef.current?.click()}
          className="mt-2"
        >
          <Plus className="h-3.5 w-3.5" />
          Add another file
        </Button>
      )}
    </div>
  );
}
